/**
 * 근태 실시간 현황 쿼리 키 팩토리.
 *
 * 루트가 둘이다 — `team-status`(현황 조회 3종)와 `teamOpsMonitoring`(팀 운영 콘솔의
 * 워크로그 모니터링). 서로 무효화 대상이 아니라 **팩토리도 둘**로 나눈다.
 *
 * ⚠️ **`attendanceKeys`(루트 `attendances`)와 별개다.** 근태 mutation 이 `attendances` 를
 * 광역 무효화해도 현황은 안 걸리고, 그 반대도 마찬가지다 — 기존 구조를 그대로 지켰다.
 * `attendance-audit` 이 근태 전용 별도 루트인 것과 같은 이유다(문서 §10-6-1 주의사항).
 *
 * 소비처가 `features/check-in` · `features/offboarding` · `features/team-settings` ·
 * `widgets/team-ops` · `pages/AttendancePage` 로 **여러 슬라이스에 퍼져 있어** 키를
 * entity 레이어에 둔다.
 *
 * 회귀 가드: `__tests__/teamStatusKeys.test.ts`.
 */
import { defineQueryKeys } from '@/shared/lib/queryKeys';

export const teamStatusKeys = defineQueryKeys('team-status', (all) => ({
  /**
   * 내 팀(또는 특정 팀) 현황 — `GET /attendance/team-status`.
   *
   * **루트 그 자체가 키**다. 무효화 7곳도 같은 값을 쓰므로 "이 조회만 무효화" 와
   * "도메인 전체" 가 같은 뜻이다. 조회가 늘면 그때 세그먼트를 붙일 것(§10-2 함정 ①).
   */
  mine: () => [...all] as const,

  /** 여러 팀 묶음 조회 — `GET /attendance/team-status/bulk`. */
  bulk: (teamIds: string[]) => [...all, 'bulk', teamIds] as const,

  /**
   * 전사 팀 현황 — `GET /attendance/all-teams-status` (관리자 근태 화면).
   *
   * 🔴 **세그먼트를 붙였다.** 예전에는 루트 **밖**인 `['all-teams-status']` 였다. 현황을
   * 바꾸는 무효화 7곳(체크인 · 퇴사 · 복직 · 근태집계 제외 · 계정 삭제/비활성화/복구)이
   * 전부 `['team-status']` 를 부르는데, 이 키만 접두사 밖이라 **한 건도 닿지 않았다.**
   * 관리자가 화면을 열어둔 채 위 작업을 하면 전사 현황이 `staleTime` 60초가 지날 때까지
   * 옛 값으로 남았다.
   *
   * 같은 파일의 `bulk` 는 이미 하위에 있어서 걸렸다 — 일관성도 없었다.
   */
  allTeams: () => [...all, 'all-teams'] as const,
}));

/**
 * 팀 운영 콘솔의 워크로그 모니터링.
 *
 * `workLogs` 접두사 **밖**인 것은 의도다 — `useWorklogMonitoringAdapter` 도크스트링 참고.
 * 워크로그 무효화가 이 집계까지 날리지 않도록 루트를 분리해 뒀고, 대신 명시 무효화가
 * 유일한 갱신 경로다(`useManagementAdapter`).
 */
export const teamOpsMonitoringKeys = defineQueryKeys('teamOpsMonitoring', (all) => ({
  range: (teamIds: string[], fromIso: string, toIso: string) =>
    [...all, teamIds, fromIso, toIso] as const,
}));
